import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import axios, { AxiosRequestConfig } from 'axios';
import {
  TResources,
  useAgentApplicantOfficeMemberIdHook,
} from '../context/AgentApplicantOfficeMember/context';
import { useCsrfToken } from '../context/useCsrfToken';

export const AXIOS_INSTANCE: AxiosInstance = axios.create();

export type CustomHeaders = AxiosRequestConfig['headers'] & {
  officeMemberId?: string;
  csrfToken?: string;
  agentApplicantResource?: TResources;
};

export type AxiosCustomOptions = {
  headers?: CustomHeaders;
  timeout?: number;
  timeoutMessage?: string;
  responseType?: AxiosRequestConfig['responseType'];
};

type CustomClient<T> = (
  data: AxiosRequestConfig,
  options?: AxiosCustomOptions
) => Promise<AxiosResponse<T>>;

export const useCustomClient = <T>(): CustomClient<T> => {
  const csrfToken = useCsrfToken();
  const getAgentApplicantOfficeMemberId = useAgentApplicantOfficeMemberIdHook();
  return async (config: AxiosRequestConfig, options?: AxiosCustomOptions) => {
    if (options?.headers) {
      const {
        officeMemberId: officeMemberId,
        agentApplicantResource,
        ...headers
      } = options.headers;
      const agentApplicantOfficeMemberId = getAgentApplicantOfficeMemberId(
        agentApplicantResource
      );
      config.headers = {
        ...config.headers,
        ...headers,
        ...(agentApplicantOfficeMemberId || officeMemberId
          ? {
              'X-Agent-Applicant-Office-Member-Id':
                agentApplicantOfficeMemberId || officeMemberId,
            }
          : {}),
      };
    }
    config.headers = {
      ...(config.headers ?? {}),
      ...(config.method?.toLowerCase() !== 'get' && csrfToken
        ? {
            'X-CSRF-Token': csrfToken,
          }
        : {}),
    };
    config.timeout = options?.timeout;
    /** Add responseType to config */
    if (options?.responseType) {
      config.responseType = options?.responseType;
    }
    const source = axios.CancelToken.source();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promise = AXIOS_INSTANCE<any, AxiosResponse<T>>({
      ...config,
      cancelToken: source.token,
    }).catch((err: AxiosError<T>) => {
      if (err.response && err.response.status < 500) {
        // TODO: 401,403の場合は再ログインを促す
        return err.response;
      }

      if (err.code === 'ECONNABORTED' && options?.timeoutMessage) {
        err.message = options.timeoutMessage;
      }

      throw err;
    });
    // @ts-ignore
    promise.cancel = () => {
      source.cancel('Query was cancelled by React Query');
    };
    return promise;
  };
};

// eslint-disable-next-line import/no-default-export
export default useCustomClient;

export type ErrorType<ErrorData> = ErrorData;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type BodyType<BodyData> = BodyData & { headers?: any };
